"use client";
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { CrossSVG, FarbinPlaceholderSVG, SearchSVG } from "./SVG";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ChevronUp } from "lucide-react";
import clsx from "clsx";
import { createPortal } from "react-dom";

// --- Types ---
export type Product = {
  id: string;
  part_number: string;
  slug: string;
  hero_image?: string | null;
  category_image?: string | null;
  productProperty?: ProductProperty[];
};

type ProductProperty = {
  property: { kind: string };
  option: { value_en: string; hex?: string | null };
  optionId?: string | number | null;
  propertyId?: string | number | null;
};

export type SearchBarProps = {
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
  fetcher?: (query: string) => Promise<Product[]>;
  onSelect?: (product: Product) => void;
  onQueryChange?: (query: string) => void;
  expandedWidth?: number | string;
  collapsedWidth?: number | string;
  showResults?: boolean;
  showLabel?: boolean;
  compact?: boolean;
  minChars?: number;
  debounceMs?: number;
  isClosed?: boolean;
};

export default function SearchBar({
  placeholder = "Part Number",
  autoFocus,
  className,
  inputClassName,
  fetcher,
  onSelect,
  onQueryChange,
  expandedWidth,
  collapsedWidth,
  showResults = true,
  showLabel = true,
  compact = false,
  minChars = 2,
  debounceMs = 250,
  isClosed = false,
}: SearchBarProps) {
  const router = useRouter();
  const locale = useLocale();
  const inputId = useId();
  const listboxId = useId();

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  // collapsed state respects the prop
  const [collapsed, setCollapsed] = useState<boolean>(!!isClosed);
  useEffect(() => setCollapsed(!!isClosed), [isClosed]);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isDesktop, setIsDesktop] = useState(false);
  const [mobilePortalEl, setMobilePortalEl] = useState<HTMLElement | null>(
    null,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const media = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(media.matches);
    update();
    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }
    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = document.getElementById("mobile-search-slot");
    setMobilePortalEl(el);
  }, []);

  // default fetcher (override with prop if you have your own)
  const defaultFetcher = useCallback(async (q: string) => {
    const res = await fetch(`/api/products/search?q=${encodeURIComponent(q)}`, {
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) throw new Error("Search failed");
    return (await res.json()) as Product[];
  }, []);
  const doFetch = fetcher ?? defaultFetcher;

  // debounce search
  useEffect(() => {
    if (!showResults) {
      setDebouncedQuery("");
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!query || query.trim().length < minChars) {
      setDebouncedQuery("");
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [query, minChars, debounceMs, showResults]);

  useEffect(() => {
    onQueryChange?.(query);
  }, [query, onQueryChange]);

  const searchQuery = useQuery({
    queryKey: ["product-search", debouncedQuery],
    queryFn: async (): Promise<Product[]> => {
      if (!debouncedQuery) return [];
      return doFetch(debouncedQuery);
    },
    enabled: showResults && debouncedQuery.length >= minChars,
    staleTime: 60_000,
    gcTime: 60_000,
  });
  const results = searchQuery.data ?? [];

  useEffect(() => {
    const data = searchQuery.data ?? [];
    if (debouncedQuery && data) {
      setOpen(true);
      setActiveIndex(data.length > 0 ? 0 : -1);
    }
  }, [debouncedQuery, searchQuery.data]);

  // outside click -- only collapse back to icon if we started closed
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        if (isClosed && query.trim().length === 0) {
          setCollapsed(true);
        }
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isClosed, query]);

  // keep highlighted item in view while arrowing
  useEffect(() => {
    if (!open || activeIndex < 0) return;
    const el = document.getElementById(`${listboxId}-opt-${activeIndex}`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, listboxId]);

  const handleSelect = useCallback(
    (item: Product) => {
      setQuery("");
      setActiveIndex(-1);
      setOpen(false);
      if (onSelect) onSelect(item);
      else router.push(`/${locale}/product/${encodeURIComponent(item.slug)}`);
      if (isClosed) setCollapsed(true);
    },
    [onSelect, router, locale, isClosed],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        if (!query && isClosed) {
          setCollapsed(true);
          setOpen(false);
        } else {
          setOpen(false);
        }
        return;
      }
      if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        setOpen(true);
        setActiveIndex(results.length ? 0 : -1);
        return;
      }
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, results.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        case "Enter":
          if (open && activeIndex >= 0 && results[activeIndex]) {
            e.preventDefault();
            handleSelect(results[activeIndex]);
          }
          break;
      }
    },
    [open, results, activeIndex, handleSelect, isClosed, query],
  );

  const clear = useCallback(() => {
    setQuery("");
    setOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  const highlight = useCallback(
    (text?: string | null) => {
      const t = text ?? "";
      const q = query.trim();
      if (!q) return t;
      const idx = t.toLowerCase().indexOf(q.toLowerCase());
      if (idx === -1) return t;
      return (
        <>
          {t.slice(0, idx)}
          <mark className="bg-pinkCustom/20 rounded px-0.5">
            {t.slice(idx, idx + q.length)}
          </mark>
          {t.slice(idx + q.length)}
        </>
      );
    },
    [query],
  );

  const showNoResults = useMemo(
    () =>
      !searchQuery.isFetching &&
      open &&
      query.trim().length >= minChars &&
      results.length === 0,
    [searchQuery.isFetching, open, query, minChars, results.length],
  );

  const renderResults = () =>
    showResults ? (
      <AnimatePresence>
        {open && !collapsed && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute top-10 z-50 mt-2 w-full overflow-hidden rounded-sm border border-gray-200 bg-white shadow-xl"
          >
            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              className="max-h-120 overflow-auto"
            >
              {searchQuery.isFetching && (
                <li className="px-3 py-2 text-sm text-gray-500">
                  Searching...
                </li>
              )}

              {!searchQuery.isFetching &&
                (searchQuery.data ?? []).map((p, idx) => (
                  <li
                    id={`${listboxId}-opt-${idx}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    key={p.id}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(p)}
                    className={[
                      "flex cursor-pointer flex-col",
                      idx === activeIndex ? "border-r-slateCustom/80" : "",
                    ].join(" ")}
                  >
                    <div className="my-3 flex gap-3 pl-3">
                      {p.category_image ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={p.category_image}
                          alt={p.part_number}
                          className="bg-grayCustom aspect-video h-auto w-26 shrink-0 self-start rounded object-cover"
                        />
                      ) : (
                        <div
                          className="bg-grayCustom flex aspect-video w-26 shrink-0 self-start items-center justify-center rounded"
                          aria-hidden
                        >
                          <FarbinPlaceholderSVG className="h-2/3 w-2/3" />
                        </div>
                      )}
                      <div className="flex min-w-0 flex-1 flex-col gap-2 pt-1">
                        <div>
                          <div className="truncate text-base leading-5 font-semibold tracking-wide">
                            {highlight(p.part_number)}
                          </div>
                        </div>
                        <div
                          className={clsx(
                            "flex w-fit flex-wrap items-center gap-1.5 gap-y-2 divide-x divide-gray-300 pt-2 pr-2 text-gray-700 lg:pt-0",
                          )}
                        >
                          {p.productProperty?.map((prop: ProductProperty) => {
                            const { property, option, optionId, propertyId } =
                              prop;
                            const key = `${propertyId}-${optionId}`;
                            return (
                              <span
                                className={clsx("pr-1.5 text-xs lg:text-sm")}
                                key={key}
                              >
                                {property.kind === "COLOR" ? (
                                  <span className="flex items-center space-x-1 lg:space-x-1.5">
                                    <span>{option.value_en}</span>
                                    <span
                                      className="inline-block h-4 w-4 rounded-xs lg:-translate-y-0.5"
                                      style={{
                                        backgroundColor: option.hex ?? "",
                                      }}
                                    />
                                  </span>
                                ) : (
                                  option.value_en
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`ml-auto w-[calc(100%-128px)] ${
                        idx === activeIndex
                          ? "lg:bg-pinkCustom bg-gray-200"
                          : "bg-gray-200"
                      } h-px`}
                    ></span>
                  </li>
                ))}

              {showNoResults && (
                <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    ) : null;

  // when the "button" expands, focus the input
  const expand = () => {
    setCollapsed(false);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 60);
  };

  return (
    <div
      ref={rootRef}
      className={["relative", className].filter(Boolean).join(" ")}
      role="search"
    >
      {isDesktop ? (
        <>
          <motion.div
            layout
            initial={false}
            animate={{
              width: collapsed
                ? (collapsedWidth ?? (isDesktop ? 72 : 32))
                : (expandedWidth ?? "100%"),
            }}
            transition={{ duration: 0.22 }}
            className={[
              "relative flex flex-row-reverse overflow-hidden",
              collapsed ? "cursor-pointer" : "",
            ].join(" ")}
            onClick={() => {
              if (collapsed) {
                expand();
              } else {
                setCollapsed(true);
                setOpen(false);
              }
            }}
            role="search"
          >
            <div
              className={[
                "flex flex-col gap-2",
                compact ? "h-fit" : "h-17",
              ].join(" ")}
            >
              <SearchSVG
                accentColor="var(--color-gray-500)"
                className={
                  compact
                    ? "h-6 w-6 lg:mt-auto lg:h-7 lg:w-7"
                    : "h-8 w-8 lg:mt-auto lg:h-9.25 lg:w-9.25 lg:pt-1.5"
                }
              />
              {showLabel && (
                <span className="text-slateCustom text-sm tracking-wide">
                  Search
                </span>
              )}
            </div>

            <div
              className={`mr-4 flex w-full flex-row-reverse items-center rounded-sm ${
                !collapsed ? "border border-gray-200" : ""
              } ${compact ? "" : "mt-2.25"} h-fit`}
            >
              <AnimatePresence initial={false} mode="popLayout">
                {!collapsed && (
                  <motion.div
                    key="expanded"
                    className="flex w-full flex-row-reverse items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                  >
                    <motion.input
                      key="search-input"
                      ref={inputRef}
                      id={inputId}
                      className={[
                        "w-full bg-transparent p-1 px-2 outline-none",
                        "placeholder:text-gray-400",
                        inputClassName ?? "",
                      ].join(" ")}
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={onKeyDown}
                      onFocus={() => {
                        if (results.length > 0) setOpen(true);
                      }}
                      placeholder={placeholder}
                      autoFocus={autoFocus}
                      role="combobox"
                      aria-expanded={open}
                      aria-controls={listboxId}
                      aria-activedescendant={
                        activeIndex >= 0
                          ? `${listboxId}-opt-${activeIndex}`
                          : undefined
                      }
                      autoComplete="off"
                      initial={{ x: -6, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      exit={{ x: -6, opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    />
                    <span className="absolute top-4.25 left-10.5 h-2/8 w-px bg-gray-200"></span>

                    {query ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          clear();
                        }}
                        aria-label="Clear search"
                        className="ml-auto cursor-pointer rounded-full px-3 py-2"
                      >
                        <CrossSVG className="w-3" svgColor="pinkCustom" />
                      </button>
                    ) : isClosed ? (
                      <button
                        aria-label="Close search"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapsed(true);
                          setOpen(false);
                        }}
                        className="ml-auto cursor-pointer rounded-full px-1 hover:bg-gray-50"
                      >
                        <ChevronRight className="text-pinkCustom w-6" />
                      </button>
                    ) : null}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {renderResults()}
        </>
      ) : (
        <>
          <button
            type="button"
            aria-expanded={!collapsed}
            aria-controls={`${listboxId}-panel`}
            onClick={() => {
              if (collapsed) {
                expand();
              } else {
                setCollapsed(true);
                setOpen(false);
              }
            }}
            className="flex flex-col items-center gap-2"
          >
            <SearchSVG
              accentColor="var(--color-gray-500)"
              className="h-8 w-8 pt-1.5"
            />
          </button>

          {mobilePortalEl
            ? createPortal(
                <AnimatePresence>
                  {!collapsed && isClosed && (
                    <motion.div
                      id={`${listboxId}-panel`}
                      initial={{ y: -6 }}
                      animate={{ y: 0 }}
                      exit={{ y: -6 }}
                      transition={{ duration: 0.18 }}
                      className="w-full bg-white/95 py-2 backdrop-blur-sm"
                    >
                      <div className="mx-auto w-full px-4">
                        <div className="relative">
                          <motion.div
                            ref={panelRef}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="origin-top overflow-hidden"
                          >
                            <div className="flex w-full flex-row-reverse items-center rounded-sm border border-gray-200 bg-white">
                              <motion.input
                                key="search-input"
                                ref={inputRef}
                                id={inputId}
                                className={[
                                  "w-full bg-transparent p-2 px-3 outline-none placeholder:text-gray-400",
                                  inputClassName ?? "",
                                ].join(" ")}
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={onKeyDown}
                                onFocus={() => {
                                  if (results.length > 0) setOpen(true);
                                }}
                                placeholder={placeholder}
                                autoFocus={autoFocus}
                                role="combobox"
                                aria-expanded={open}
                                aria-controls={listboxId}
                                aria-activedescendant={
                                  activeIndex >= 0
                                    ? `${listboxId}-opt-${activeIndex}`
                                    : undefined
                                }
                                autoComplete="off"
                                initial={{ x: -6, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                exit={{ x: -6, opacity: 0 }}
                                transition={{ duration: 0.18 }}
                              />
                              <span className="absolute top-4.25 left-10.5 h-2/8 w-px bg-gray-200"></span>

                              {query ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clear();
                                  }}
                                  aria-label="Clear search"
                                  className="ml-auto cursor-pointer rounded-full px-3 py-2"
                                >
                                  <CrossSVG
                                    className="w-3"
                                    svgColor="pinkCustom"
                                  />
                                </button>
                              ) : isClosed ? (
                                <button
                                  aria-label="Close search"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCollapsed(true);
                                    setOpen(false);
                                  }}
                                  className="ml-auto cursor-pointer rounded-full px-1 hover:bg-gray-50"
                                >
                                  <ChevronUp className="text-pinkCustom w-6" />
                                </button>
                              ) : null}
                            </div>
                          </motion.div>

                          {renderResults()}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>,
                mobilePortalEl,
              )
            : null}
        </>
      )}
    </div>
  );
}
